// src/services/address/gps-external/jarvisly/adapter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Jarvisly GPS Provider Adapter (Backend Google Maps)
 * Calls internal backend endpoint that uses Google Maps API
 *
 * Implements provider contract:
 * - searchCoordinates(payload) → Promise<Object>
 * - normalizeRequestPayload(payload) → Object
 * - normalizeResponseBody(response) → Universal POJO
 */

/**
 * Searches GPS coordinates for given address
 * @param {Object} payload - Search parameters
 * @param {string} payload.address - Street address
 * @param {string} payload.number - Street number
 * @param {string} payload.city - City name
 * @param {string} payload.region - State/region code (e.g., 'SP', 'RJ')
 * @returns {Promise<Object>} Universal POJO with coordinates
 */
async function searchCoordinates(payload) {
  const normalizedRequest = normalizeRequestPayload(payload);

  // Call backend endpoint via axiosService.post
  // Returns [data, axiosResponse] - we need just data
  const response = await axiosService.post({
    url: API.PROVIDERS.GPS.JARVISLY,
    data: normalizedRequest,
  });

  // Return raw response - factory will call normalizeResponseBody
  return response;
}

/**
 * Normalizes incoming payload to backend format
 * @param {Object} payload - Raw payload
 * @returns {Object} Backend-compatible payload
 */
function normalizeRequestPayload(payload) {
  const { address, number, city, region } = payload;

  // Build full address string for backend
  const addressParts = [
    address && number ? `${address}, ${number}` : address || '',
    city || '',
    region || '',
    'Brasil',
  ].filter(Boolean);

  return {
    fullAddress: addressParts.join(', '),
    address: address || '',
    number: number || '',
    city: city || '',
    region: region || '',
  };
}

/**
 * Normalizes backend response to universal POJO
 * @param {Object} response - Backend response
 * @returns {Object} Universal POJO
 *
 * Backend response structure:
 * {
 *   success: boolean,
 *   data: {
 *     ok: boolean,
 *     provider: "google",
 *     latitude: number,
 *     longitude: number,
 *     coordinates: [lat, lng],
 *     formattedAddress: string,
 *     success: boolean,
 *     error: string | null
 *   }
 * }
 *
 * Universal POJO structure for GPS:
 * {
 *   provider: string,
 *   success: boolean,
 *   apiWorked: boolean,
 *   coordinates: [lat, lng] | null,
 *   error: string | null,
 *   source: string
 * }
 */
function normalizeResponseBody(response) {
  // axiosService.post returns [data, axiosResponse]
  // axiosService already unwraps the backend envelope, so response[0] is the GPS data directly
  const gpsData = Array.isArray(response) ? response[0] : response;

  // GPS data not found (address not found or insufficient data)
  if (!gpsData || gpsData.success === false || gpsData.error || !gpsData.ok) {
    return {
      provider: 'jarvisly',
      success: false,
      apiWorked: true, // API worked, but address not found
      coordinates: null,
      error: gpsData?.error || 'Coordenadas não encontradas',
      source: 'jarvisly',
    };
  }

  // Success - coordinates found
  // Backend already returns coordinates as [lat, lng] array
  return {
    provider: 'jarvisly',
    success: true,
    apiWorked: true,
    coordinates: gpsData.coordinates || [
      parseFloat(gpsData.latitude),
      parseFloat(gpsData.longitude),
    ],
    error: null,
    source: gpsData.provider || 'jarvisly', // Could be "google", "nominatim", etc from backend
    formattedAddress: gpsData.formattedAddress,
    originalAddress: gpsData.originalAddress,
  };
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCoordinates,
  normalizeRequestPayload,
  normalizeResponseBody,
};
